import { formatPrice } from "ui";

/**
 * Shared form-data and validation shapes for the Create Team wizard
 * (Phase B).
 *
 * Pricing: the per-seat Price at the chosen interval. Regional creators
 * pay the regional team Price; everyone else pays the standard Pro Price
 * per seat. The wizard's live preview pulls the matching price from
 * useStripeStore (`team_monthly` / `team_yearly`) and sends its priceId,
 * which the backend checks, so display always matches what Stripe will
 * charge.
 */

export type BillingInterval = "monthly" | "yearly";

export interface CreateTeamFormData {
  firmName: string;
  domain: string;
  seats: number;
  interval: BillingInterval;
}

export interface CreateTeamErrors {
  firmName?: string;
  domain?: string;
  seats?: string;
}

export const MIN_TEAM_SEATS = 2;
export const MAX_TEAM_SEATS = 100;
export const MAX_FIRM_NAME_LEN = 200;
export const MAX_DOMAIN_LEN = 200;

/**
 * Fallback price (in smallest currency unit, e.g. cents) used by the
 * preview math when useStripeStore hasn't hydrated yet. Approximate
 * placeholder values matching the historical USD Pro rates. The actual
 * charge always uses Stripe's resolved Price, not these.
 */
export const FALLBACK_PRICE_PER_SEAT = {
  monthly: 1900, // ~$19
  yearly: 15000, // ~$150
} as const;

/** Validate a team name. Returns an error string, or undefined when valid. */
export function validateName(name: string): string | undefined {
  const trimmed = name.trim();
  if (!trimmed) return "Team name is required";
  if (trimmed.length > MAX_FIRM_NAME_LEN) {
    return `Team name must be ${MAX_FIRM_NAME_LEN} characters or fewer`;
  }
  return undefined;
}

/** The company website as the bare domain invites are limited to: "https://www.Acme.com/about" → "acme.com". */
export const normalizeWebsite = (website: string): string =>
  website
    .trim()
    .toLowerCase()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//, "")
    .split(/[/?#]/)[0]
    .replace(/^.*@/, "")
    .replace(/:\d*$/, "")
    .replace(/^www\./, "")
    .replace(/\.$/, "");

/** Validate the (required) company website. Returns an error string, or undefined when valid. */
export function validateDomain(website: string): string | undefined {
  const domain = normalizeWebsite(website);
  if (!domain) return "Enter your company website";
  if (domain.length > MAX_DOMAIN_LEN) {
    return `Website must be ${MAX_DOMAIN_LEN} characters or fewer`;
  }
  if (!/^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}$/.test(domain)) {
    return "Enter a valid website, like acme.com";
  }
  return undefined;
}

export function validateNameAndDomain(data: CreateTeamFormData): CreateTeamErrors {
  const errors: CreateTeamErrors = {};
  const nameError = validateName(data.firmName);
  if (nameError) errors.firmName = nameError;
  const domainError = validateDomain(data.domain);
  if (domainError) errors.domain = domainError;
  return errors;
}

export function validateSeats(data: CreateTeamFormData): CreateTeamErrors {
  const errors: CreateTeamErrors = {};
  if (!Number.isInteger(data.seats)) {
    errors.seats = "Seats must be a whole number";
  } else if (data.seats < MIN_TEAM_SEATS) {
    errors.seats = `Minimum ${MIN_TEAM_SEATS} seats`;
  } else if (data.seats > MAX_TEAM_SEATS) {
    errors.seats = `Maximum ${MAX_TEAM_SEATS} seats`;
  }
  return errors;
}

export const hasErrors = (errors: CreateTeamErrors): boolean =>
  Object.keys(errors).length > 0;

/** Total in smallest unit (cents etc.) for the selected interval. */
export const computeTotal = (
  seats: number,
  pricePerSeat: number,
): number => seats * pricePerSeat;

/** Currency-aware format. Wraps the ui formatPrice helper. */
export const formatMoney = (amountSmallestUnit: number, currency: string): string =>
  formatPrice(amountSmallestUnit, currency || "usd", true, "currency");

/** Like formatMoney, but whole amounts drop the ".00" (₹7,290, yet $19.99 stays exact). */
export const formatMoneyShort = (amountSmallestUnit: number, currency: string): string =>
  formatPrice(amountSmallestUnit, currency || "usd", amountSmallestUnit % 100 !== 0, "currency");
